import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { adminApi } from '../api/endpoints';

function formatWhen(s) {
  if (!s) return '—';
  // SQLite: "YYYY-MM-DD HH:MM:SS"
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!m) return String(s);
  return `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}`;
}

function eventTypeLabel(t) {
  if (t === 'login') return 'вход';
  if (t === 'access') return 'открытие';
  return t || '—';
}

export default function AdminUsers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [events, setEvents] = useState([]);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [loading, setLoading] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'viewer' });
  const [pwdFor, setPwdFor] = useState(null);
  const [pwdVal, setPwdVal] = useState('');
  const [filterUser, setFilterUser] = useState('');

  useEffect(() => {
    if (user && user.role !== 'admin') navigate('/', { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    setErr('');
    try {
      const [u, e] = await Promise.all([
        adminApi.listUsers(),
        adminApi.listLoginEvents({
          limit: 300,
          ...(filterUser ? { username: filterUser } : {}),
        }),
      ]);
      setRows(u.data || []);
      setEvents(Array.isArray(e.data) ? e.data : []);
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message || 'Ошибка');
    }
  }, [filterUser]);

  useEffect(() => {
    if (user?.role === 'admin') load();
  }, [user, load]);

  if (!user || user.role !== 'admin') return null;

  const create = async () => {
    setErr('');
    setOk('');
    setLoading(true);
    try {
      await adminApi.createUser({
        username: newUser.username.trim(),
        password: newUser.password,
        role: newUser.role,
      });
      setOk(`Создан пользователь «${newUser.username.trim()}»`);
      setNewUser({ username: '', password: '', role: 'viewer' });
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === 'string' ? d : (e.message || 'Ошибка'));
    }
    setLoading(false);
  };

  const remove = async (username) => {
    if (!window.confirm(`Удалить пользователя «${username}»?`)) return;
    setErr('');
    setOk('');
    try {
      await adminApi.deleteUser(username);
      setOk(`Удалён «${username}»`);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Ошибка');
    }
  };

  const savePwd = async (username) => {
    setErr('');
    setOk('');
    try {
      await adminApi.setUserPassword(username, { password: pwdVal });
      setOk(`Пароль обновлён для «${username}»`);
      setPwdFor(null);
      setPwdVal('');
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Ошибка');
    }
  };

  return (
    <div>
      <h1 style={styles.h1}>Пользователи</h1>
      <p style={styles.lead}>
        Управление доступом и журнал входов в дашборд: кто, когда и с какого устройства.
        Пароли — bcrypt-хеш в <code style={styles.code}>dasna.db</code>.
      </p>

      {err && <div style={styles.error}>{err}</div>}
      {ok && <div style={styles.ok}>{ok}</div>}

      <div style={styles.card}>
        <h2 style={styles.h2}>Новый пользователь</h2>
        <div style={styles.row}>
          <label style={styles.lbl}>Логин</label>
          <input
            style={styles.input}
            value={newUser.username}
            onChange={(e) => setNewUser((u) => ({ ...u, username: e.target.value }))}
            autoComplete="off"
          />
        </div>
        <div style={styles.row}>
          <label style={styles.lbl}>Пароль</label>
          <input
            style={styles.input}
            type="password"
            value={newUser.password}
            onChange={(e) => setNewUser((u) => ({ ...u, password: e.target.value }))}
            autoComplete="new-password"
          />
        </div>
        <div style={styles.row}>
          <label style={styles.lbl}>Роль</label>
          <select
            style={styles.input}
            value={newUser.role}
            onChange={(e) => setNewUser((u) => ({ ...u, role: e.target.value }))}
          >
            <option value="viewer">viewer (дашборды)</option>
            <option value="admin">admin</option>
          </select>
        </div>
        <button
          type="button"
          disabled={loading || !newUser.username.trim() || !newUser.password}
          onClick={create}
          style={styles.btnPrimary}
        >
          Создать
        </button>
      </div>

      <div style={{ ...styles.card, maxWidth: 960 }}>
        <h2 style={styles.h2}>Список</h2>
        <button type="button" onClick={load} style={styles.btnSecondary}>Обновить</button>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Логин</th>
                <th style={styles.th}>Роль</th>
                <th style={styles.th}>Последний визит</th>
                <th style={styles.th}>Устройство</th>
                <th style={styles.th}>IP</th>
                <th style={styles.th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.username}>
                  <td style={styles.td}>{r.username}</td>
                  <td style={styles.td}>{r.role}</td>
                  <td style={styles.td}>{formatWhen(r.last_seen_at)}</td>
                  <td style={styles.tdMuted}>{r.last_device || '—'}</td>
                  <td style={styles.tdMuted}>{r.last_ip || '—'}</td>
                  <td style={styles.tdRight}>
                    <button
                      type="button"
                      style={styles.linkBtn}
                      onClick={() => setFilterUser(r.username)}
                      title="Показать историю этого пользователя"
                    >
                      История
                    </button>
                    <button type="button" style={styles.linkBtn} onClick={() => { setPwdFor(r.username); setPwdVal(''); }}>
                      Сменить пароль
                    </button>
                    {r.username !== user.username && (
                      <button type="button" style={styles.dangerBtn} onClick={() => remove(r.username)}>
                        Удалить
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ ...styles.card, maxWidth: 1100 }}>
        <div style={styles.histHead}>
          <h2 style={{ ...styles.h2, marginBottom: 0 }}>История входов</h2>
          <div style={styles.histFilters}>
            <select
              style={{ ...styles.input, maxWidth: 220, marginBottom: 0 }}
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
            >
              <option value="">Все пользователи</option>
              {rows.map((r) => (
                <option key={r.username} value={r.username}>{r.username}</option>
              ))}
            </select>
            <button type="button" onClick={load} style={{ ...styles.btnSecondary, marginBottom: 0 }}>
              Обновить
            </button>
          </div>
        </div>
        <p style={styles.hint}>
          «Вход» — логин по паролю. «Открытие» — заход в дашборд с сохранённой сессией
          (не чаще раза в 30 мин с одного устройства).
        </p>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Когда</th>
                <th style={styles.th}>Кто</th>
                <th style={styles.th}>Тип</th>
                <th style={styles.th}>Устройство</th>
                <th style={styles.th}>IP</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && (
                <tr>
                  <td style={styles.tdMuted} colSpan={5}>Пока нет записей — появятся после следующего входа.</td>
                </tr>
              )}
              {events.map((ev) => (
                <tr key={ev.id}>
                  <td style={styles.td}>{formatWhen(ev.created_at)}</td>
                  <td style={styles.td}>{ev.username}</td>
                  <td style={styles.td}>{eventTypeLabel(ev.event_type)}</td>
                  <td style={styles.td} title={ev.user_agent || ''}>{ev.device_label || '—'}</td>
                  <td style={styles.tdMuted}>{ev.ip || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {pwdFor && (
        <div style={styles.card}>
          <h2 style={styles.h2}>Новый пароль для «{pwdFor}»</h2>
          <input
            style={styles.input}
            type="password"
            value={pwdVal}
            onChange={(e) => setPwdVal(e.target.value)}
            placeholder="Новый пароль"
          />
          <button type="button" style={styles.btnPrimary} disabled={!pwdVal} onClick={() => savePwd(pwdFor)}>
            Сохранить
          </button>
          <button type="button" style={{ ...styles.btnSecondary, marginLeft: 10 }} onClick={() => { setPwdFor(null); setPwdVal(''); }}>
            Отмена
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  h1: { color: 'var(--text-primary)', fontSize: 22, fontWeight: 700, marginBottom: 12, letterSpacing: -0.3 },
  lead: { color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.5, marginBottom: 22, maxWidth: 720 },
  code: { fontSize: 12, color: 'var(--accent)' },
  card: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 14,
    padding: 20,
    marginBottom: 18,
    maxWidth: 640,
  },
  h2: { color: 'var(--text-primary)', fontSize: 15, fontWeight: 600, marginBottom: 14 },
  row: { marginBottom: 12 },
  lbl: { display: 'block', color: 'var(--text-muted)', fontSize: 11, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 },
  input: {
    width: '100%',
    maxWidth: 360,
    boxSizing: 'border-box',
    background: 'var(--bg-input)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '9px 12px',
    fontSize: 14,
    outline: 'none',
  },
  btnPrimary: {
    background: 'var(--accent)',
    color: '#0a0e14',
    border: 'none',
    borderRadius: 10,
    padding: '10px 18px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 8,
  },
  btnSecondary: {
    background: 'var(--accent-dim)',
    color: 'var(--accent)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: 14,
  },
  error: {
    color: 'var(--danger)',
    background: 'rgba(248,113,113,.1)',
    border: '1px solid rgba(248,113,113,.2)',
    padding: '12px 16px',
    borderRadius: 10,
    marginBottom: 16,
    fontSize: 13,
    maxWidth: 640,
  },
  ok: {
    color: 'var(--text-primary)',
    background: 'rgba(34,211,238,.08)',
    border: '1px solid rgba(34,211,238,.2)',
    padding: '12px 16px',
    borderRadius: 10,
    marginBottom: 16,
    fontSize: 13,
    maxWidth: 640,
  },
  tableWrap: { overflowX: 'auto', marginTop: 8 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', whiteSpace: 'nowrap' },
  td: { padding: '10px 10px', borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' },
  tdMuted: { padding: '10px 10px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: 12 },
  tdRight: { padding: '8px 10px', borderBottom: '1px solid var(--border)', textAlign: 'right', whiteSpace: 'nowrap' },
  linkBtn: {
    background: 'transparent',
    border: 'none',
    color: 'var(--accent)',
    cursor: 'pointer',
    fontSize: 13,
    marginRight: 12,
  },
  dangerBtn: {
    background: 'transparent',
    border: 'none',
    color: 'var(--danger)',
    cursor: 'pointer',
    fontSize: 13,
  },
  histHead: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 10,
  },
  histFilters: { display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' },
  hint: { color: 'var(--text-muted)', fontSize: 12, lineHeight: 1.45, marginBottom: 8, marginTop: 0 },
};
